import React, { useState } from "react";
import { Form } from "antd";
import { Icon } from "@iconify/react";
import { getErrorProps } from "@utils/helpers";
import { InputText, ButtonComponent, CancelButton, ModalComponent } from "@comps/components";
import { makeRequest, notify } from "@utils/helpers";
import { saveStclFileReq } from "../requests";

const formName = "submitStlcCase";

/**
 * Hand over the case's STL, as a link to it.
 *
 * The file is not sent through the app — a case is an upper and a lower
 * arch and routinely runs to hundreds of megabytes, so it is hosted
 * where files that size belong (WeTransfer, Drive, a lab's own host) and
 * the link is what is handed over. It lands on the case's file_link and
 * is posted to the case chat, and completing a case requires it.
 *
 * Wears the same shell as every other dialog in the app — it was built
 * on a raw antd Modal with its own inline padding, so it arrived without
 * the icon chip, the subtitle or the title face the rest of them have.
 */
const SubmitStlcCase = (props) => {
  const [errors, setErrors] = useState([]);
  const [loader, setLoader] = useState(false);

  const replacing = Boolean(props.currentLink);

  const rules = {
    filelink: [
      { required: true, message: "Paste the link to the STL file" },
      { type: "url", message: "That is not a link — it needs the full address, starting with https://" },
    ],
  };

  const onFinish = (data) => {
    let payload = {
      caseid: props.caseid,
      filelink: data.filelink
    }
    makeRequest(setLoader, saveStclFileReq, payload, onSuccess, onError);
  };

  const onSuccess = (data, res) => {
    notify("STL file submitted", res.msg);
    // Callers that show the case's own delivery state need to re-read it
    // — closing the dialog alone would leave the page saying the file is
    // still missing.
    if (props.onSubmitted) {
      props.onSubmitted(data);
      return;
    }
    props.handleCancel();
  }

  const onError = (data) => {
    setErrors(data);
  }

  const footer = (
    <div className="form-actions">
      <CancelButton key="close_button" onClick={ () => props.handleCancel(false) } />
      <ButtonComponent
        key="create_button"
        form={ formName }
        htmlType="submit"
        type="primary"
        state={ loader }
      >
        { replacing ? "Replace link" : "Submit STL file" }
      </ButtonComponent>
    </div>
  );

  return (
    <ModalComponent
      icon="tabler:cube"
      mainTitle={ replacing ? "Replace the STL file link" : "Submit the STL file" }
      subTitle={ replacing
        ? "The case will point at the new link instead"
        : "Upload it to WeTransfer or Drive, then paste the link here" }
      open={ true }
      onCancel={ props.handleCancel }
      width="min(560px, calc(100vw - 32px))"
      footer={ footer }
    >
      <Form
        layout="vertical"
        name={ formName }
        initialValues={ { filelink: props.currentLink } }
        onFinish={ onFinish }
      >
        <Form.Item
          name="filelink"
          rules={ rules.filelink }
          label="Link to the file"
          className="da-mb-0"
          onChange={ () => setErrors([]) }
          { ...getErrorProps(errors['filelink']) }
        >
          <InputText placeholder="https://wetransfer.com/downloads/..." />
        </Form.Item>
      </Form>

      <p className="confirm-outcome">
        <Icon icon="tabler:arrow-narrow-right" width={ 17 } />
        <span>
          The link is posted to the case chat, and the case can be completed once it is in.
          Check it opens for someone who is not signed in to your account.
        </span>
      </p>
    </ModalComponent>
  );
};

export default SubmitStlcCase;
